import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { ST } from 'types';
import './GForceDisplay.css';

interface GForceData {
  timeMs: number;
  gForce: number; // in g (Earth gravity units)
}

interface GForceDisplayProps {
  record: any; // RunModel
  currentTimeMs: number;
  isVisible: boolean;
}

const GForceDisplay: React.FC<GForceDisplayProps> = observer(({
  record,
  currentTimeMs,
  isVisible
}) => {
  const [currentGForce, setCurrentGForce] = useState<number>(1.0); // Start at 1g (normal gravity)

  // Calculate G-Force data from playData steps
  const gForceData = useMemo((): GForceData[] => {
    if (!record?.playData?.steps || record.playData.steps.length < 3) {
      return [];
    }

    const steps = record.playData.steps as ST.PlayStep[];
    const data: GForceData[] = [];

    for (let i = 1; i < steps.length - 1; i++) {
      const prevStep = steps[i - 1];
      const currentStep = steps[i];
      const nextStep = steps[i + 1];

      if (!prevStep.s_m || !currentStep.s_m || !nextStep.s_m || 
          !prevStep.t_ms || !currentStep.t_ms || !nextStep.t_ms) {
        continue;
      }

      // Calculate time differences (convert ms to seconds)
      const dt1 = (currentStep.t_ms - prevStep.t_ms) / 1000;
      const dt2 = (nextStep.t_ms - currentStep.t_ms) / 1000;
      
      if (dt1 <= 0 || dt2 <= 0) continue;

      // Calculate velocities from position differences (m/s)
      const vx1 = ((currentStep.s_m.x || 0) - (prevStep.s_m.x || 0)) / dt1;
      const vy1 = ((currentStep.s_m.y || 0) - (prevStep.s_m.y || 0)) / dt1;
      const vz1 = ((currentStep.s_m.z || 0) - (prevStep.s_m.z || 0)) / dt1;

      const vx2 = ((nextStep.s_m.x || 0) - (currentStep.s_m.x || 0)) / dt2;
      const vy2 = ((nextStep.s_m.y || 0) - (currentStep.s_m.y || 0)) / dt2;
      const vz2 = ((nextStep.s_m.z || 0) - (currentStep.s_m.z || 0)) / dt2;

      // Calculate accelerations (m/s²)
      const ax = (vx2 - vx1) / ((dt1 + dt2) / 2);
      const ay = (vy2 - vy1) / ((dt1 + dt2) / 2);
      const az = (vz2 - vz1) / ((dt1 + dt2) / 2);

      // Calculate total acceleration magnitude
      const totalAcceleration = Math.sqrt(ax * ax + ay * ay + (az + 9.81) * (az + 9.81));
      
      // Convert to g-units (g = 9.81 m/s²)
      const gForce = totalAcceleration / 9.81;

      data.push({
        timeMs: currentStep.t_ms,
        gForce: Math.max(0.1, Math.min(8.0, gForce)) // Clamp between 0.1g and 8g
      });
    }

    return data;
  }, [record?.playData?.steps]);

  // Update current G-Force based on time
  useEffect(() => {
    if (gForceData.length === 0) {
      setCurrentGForce(1.0);
      return;
    }

    // Find closest G-Force value for current time
    let closestData = gForceData[0];
    let minTimeDiff = Math.abs(currentTimeMs - gForceData[0].timeMs);

    for (const data of gForceData) {
      const timeDiff = Math.abs(currentTimeMs - data.timeMs);
      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        closestData = data;
      }
    }

    // Interpolate between nearby points for smoother animation
    const nextIndex = gForceData.findIndex(d => d.timeMs > currentTimeMs);
    const prevIndex = nextIndex - 1;

    let targetGForce = closestData.gForce;

    if (nextIndex > 0 && prevIndex >= 0) {
      const prevData = gForceData[prevIndex];
      const nextData = gForceData[nextIndex];
      const timeFraction = (currentTimeMs - prevData.timeMs) / (nextData.timeMs - prevData.timeMs);
      targetGForce = prevData.gForce + (nextData.gForce - prevData.gForce) * timeFraction;
    }

    // Apply simple smoothing to reduce jitter
    setCurrentGForce(prev => {
      const smoothingFactor = 0.3; // 0 = no smoothing, 1 = instant update
      const newValue = prev + (targetGForce - prev) * smoothingFactor;
      // Only update if the change is significant to avoid tiny updates
      if (Math.abs(newValue - prev) > 0.01) {
        return newValue;
      }
      return prev;
    });
  }, [currentTimeMs]);

  if (!isVisible || gForceData.length === 0) {
    return null;
  }

  // Calculate bar height (0-100%)
  const maxGForce = 5.0; // Maximum G-Force for display scaling
  const barHeight = Math.min(100, (currentGForce / maxGForce) * 100);
  
  // Color coding based on G-Force intensity
  const getBarColor = (gForce: number): string => {
    if (gForce < 1.5) return '#4CAF50'; // Green - Normal
    if (gForce < 2.5) return '#FF9800'; // Orange - Moderate
    if (gForce < 4.0) return '#FF5722'; // Red-Orange - High
    return '#F44336'; // Red - Extreme
  };

  const barColor = getBarColor(currentGForce);

  return (
    <div 
      className="gforce-display" 
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      <div className="gforce-container">
        {/* G-Force Bar */}
        <div className="gforce-bar-container">
          <div 
            className="gforce-bar"
            style={{
              height: `${barHeight}%`,
              backgroundColor: barColor,
              transition: 'height 0.1s ease-out, background-color 0.2s ease'
            }}
          />
          {/* Scale markers */}
          <div className="gforce-scale">
            {[1, 2, 3, 4, 5].map(g => (
              <div 
                key={g}
                className="scale-marker"
                style={{ bottom: `${(g / maxGForce) * 100}%` }}
              >
                <span className="scale-label">{g}g</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Current Value Display */}
        <div className="gforce-value">
          <span className="gforce-number">{currentGForce.toFixed(1)}g</span>
          {/* <span className="gforce-unit">g</span> */}
        </div>
        
        {/* Label */}
        {/* <div className="gforce-label">G-Force</div> */}
      </div>
    </div>
  );
});

export default GForceDisplay;
